// Cleans up a score that MuseScore imported from MIDI and brings back what the MIDI only implies:
// title, accents (louder notes), sustain pedal marks, fermatas and rit. (tempo dips) and dynamics.

import type {PedalEvent} from "./midi";
import type {Timeline} from "./player";

interface Placed {
    el: Element;
    // Quarter notes from the start
    at: number;
    duration: number;
    staff: number;
    measure: number;
    // First note of the chord this note belongs to; directions and marks go there
    head: Element;
    velocity: number | null;
    struck: boolean;
    divisions: number;
}

interface Layout {
    measures: Element[];
    notes: Placed[];
    directions: Array<{ el: Element; at: number }>;
}

const DYNAMICS: Array<[string, number]> = [["pp", 33], ["p", 49], ["mp", 64], ["mf", 80], ["f", 96], ["ff", 112]];
// MuseScore writes velocity as a percentage of 90 in the note's dynamics attribute
const VELOCITY_PER_DYNAMICS = 0.9;
const ACCENT_MARGIN = 10;

function children(el: Element, name: string): Element[] {
    return Array.from(el.children).filter(c => c.tagName === name);
}

function child(el: Element, name: string): Element | undefined {
    return children(el, name)[0];
}

function num(el: Element | undefined, name: string, fallback = 0): number {
    const value = parseFloat((el && child(el, name)?.textContent) ?? "");
    return Number.isFinite(value) ? value : fallback;
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
}

// Builds an element from a tag, attributes and children (elements or text)
function make(doc: Document, tag: string, attrs: Record<string, string> = {}, ...kids: Array<Element | string>): Element {
    const el = doc.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    kids.forEach(k => el.appendChild(typeof k === "string" ? doc.createTextNode(k) : k));
    return el;
}

function layout(part: Element): Layout {
    const result: Layout = {measures: children(part, "measure"), notes: [], directions: []};
    let divisions = 1;
    let start = 0;
    result.measures.forEach((measure, index) => {
        let cursor = 0;
        let lastOnset = 0;
        let length = 0;
        let head: Element | null = null;
        for (const el of Array.from(measure.children)) {
            if (el.tagName === "attributes") {
                divisions = num(el, "divisions", divisions) || divisions;
            } else if (el.tagName === "backup") {
                cursor -= num(el, "duration");
            } else if (el.tagName === "forward") {
                cursor += num(el, "duration");
            } else if (el.tagName === "direction") {
                result.directions.push({el, at: start + cursor / divisions});
            } else if (el.tagName === "note") {
                const duration = num(el, "duration");
                const isChord = !!child(el, "chord");
                if (!isChord) {
                    lastOnset = cursor;
                    head = el;
                    if (!child(el, "grace")) cursor += duration;
                }
                const ties = children(el, "tie").map(t => t.getAttribute("type"));
                const dynamics = parseFloat(el.getAttribute("dynamics") ?? "");
                result.notes.push({
                    el,
                    at: start + lastOnset / divisions,
                    duration: duration / divisions,
                    staff: num(el, "staff", 1),
                    measure: index,
                    head: head ?? el,
                    velocity: Number.isFinite(dynamics) ? dynamics * VELOCITY_PER_DYNAMICS : null,
                    struck: !!child(el, "pitch") && !(ties.includes("stop") && !ties.includes("start")),
                    divisions,
                });
            }
            length = Math.max(length, cursor);
        }
        start += length / divisions;
    });
    return result;
}

function setTitle(doc: Document, title: string) {
    const root = doc.documentElement;
    [...children(root, "work"), ...children(root, "movement-title"), ...children(root, "credit")]
        .forEach(el => root.removeChild(el));
    if (!title) return;
    root.insertBefore(make(doc, "work", {}, make(doc, "work-title", {}, title)), root.firstElementChild);
}

const STEP_PC: Record<string, number> = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11};
const CLEFS = {G: ["G", "2"], F: ["F", "4"]} as const;
// Children of <attributes> that come before <clef>
const BEFORE_CLEF = new Set(["footnote", "level", "divisions", "key", "time", "staves", "part-symbol", "instruments"]);

function midiOf(note: Element): number | null {
    const pitch = child(note, "pitch");
    if (!pitch) return null;
    return 12 * (num(pitch, "octave", 4) + 1) + (STEP_PC[child(pitch, "step")?.textContent?.trim() ?? "C"] ?? 0)
        + Math.round(num(pitch, "alter"));
}

// MuseScore switches clef whenever a hand wanders, even for a beat. Printed piano music keeps the clef
// and adds ledger lines, changing it at a barline only when a hand stays in the other register.
function reassignClefs(part: Element, l: Layout) {
    const first = part.getElementsByTagName("attributes")[0];
    const initial = new Map<number, "G" | "F">();
    for (const clef of Array.from(part.getElementsByTagName("clef"))) {
        const staff = Number(clef.getAttribute("number") ?? 1);
        if (clef.parentElement === first && !initial.has(staff)) {
            initial.set(staff, child(clef, "sign")?.textContent?.trim() === "F" ? "F" : "G");
        } else {
            const attributes = clef.parentElement!;
            attributes.removeChild(clef);
            if (!attributes.children.length) attributes.parentElement?.removeChild(attributes);
        }
    }

    initial.forEach((start, staff) => {
        const wanted = l.measures.map((_, m) => {
            const pitches = l.notes.filter(n => n.staff === staff && n.measure === m && n.struck)
                .map(n => midiOf(n.el)!).filter(p => p !== null);
            if (!pitches.length) return null;
            const mid = median(pitches);
            return mid >= 62 ? "G" : mid <= 55 ? "F" : null;
        });
        let current = start;
        l.measures.forEach((measure, m) => {
            const want = wanted[m];
            if (m === 0 || !want || want === current || (wanted[m + 1] && wanted[m + 1] !== want)) return;
            current = want;
            const doc = measure.ownerDocument;
            let attributes = child(measure, "attributes");
            if (!attributes) {
                attributes = make(doc, "attributes");
                const print = child(measure, "print");
                measure.insertBefore(attributes, print ? print.nextSibling : measure.firstChild);
            }
            const anchor = Array.from(attributes.children).filter(c => BEFORE_CLEF.has(c.tagName)).pop();
            attributes.insertBefore(
                make(doc, "clef", {number: String(staff)}, make(doc, "sign", {}, CLEFS[want][0]),
                    make(doc, "line", {}, CLEFS[want][1])),
                anchor ? anchor.nextSibling : attributes.firstChild);
        });
    });
}

// Imported rests sometimes carry staccato dots
function cleanRests(part: Element) {
    for (const note of Array.from(part.getElementsByTagName("note"))) {
        if (!child(note, "rest")) continue;
        children(note, "notations").forEach(n => note.removeChild(n));
    }
}

// Notations must stay ahead of lyrics in a note; MuseScore exports none there, so append is enough
function notationsOf(note: Element): Element {
    return child(note, "notations") ?? note.appendChild(make(note.ownerDocument, "notations"));
}

function addAccents(notes: Placed[]) {
    const level = new Map<string, number[]>();
    const key = (staff: number, measure: number) => `${staff}:${measure}`;
    for (const n of notes) {
        if (!n.struck || n.velocity === null) continue;
        level.set(key(n.staff, n.measure), [...(level.get(key(n.staff, n.measure)) ?? []), n.velocity]);
    }
    const accented = new Set<Element>();
    for (const n of notes) {
        if (!n.struck || n.velocity === null || accented.has(n.head)) continue;
        // Neighbouring measures steady the reference level when a bar is short of notes
        const around = [-1, 0, 1].flatMap(d => level.get(key(n.staff, n.measure + d)) ?? []);
        if (n.velocity >= median(around) + ACCENT_MARGIN) accented.add(n.head);
    }
    accented.forEach(head => {
        const notations = notationsOf(head);
        const articulations = child(notations, "articulations")
            ?? notations.appendChild(make(head.ownerDocument, "articulations"));
        if (!child(articulations, "accent")) articulations.appendChild(make(head.ownerDocument, "accent"));
    });
    return accented;
}

// The chord head on this staff whose onset is closest to the time; directions are placed in front of it
function anchorAt(notes: Placed[], at: number, staff: number): Placed | undefined {
    const pick = (list: Placed[]) => list.reduce<Placed | undefined>(
        (best, n) => (!best || Math.abs(n.at - at) < Math.abs(best.at - at) ? n : best), undefined);
    const heads = notes.filter(n => n.el === n.head);
    return pick(heads.filter(n => n.staff === staff && Math.abs(n.at - at) < 4)) ?? pick(heads);
}

function insertDirection(anchor: Placed, direction: Element) {
    anchor.head.parentElement!.insertBefore(direction, anchor.head);
}

function addPedals(doc: Document, notes: Placed[], pedals: PedalEvent[], staff: number) {
    const marks: Array<{ at: number; types: string[] }> = [];
    for (let i = 0; i < pedals.length; i++) {
        const p = pedals[i];
        const next = pedals[i + 1];
        // Release and press on the same beat is a pedal change: one "✱ Ped." spot
        if (!p.down && next?.down && next.at - p.at < 0.1) {
            marks.push({at: p.at, types: ["stop", "start"]});
            i++;
        } else {
            marks.push({at: p.at, types: [p.down ? "start" : "stop"]});
        }
    }
    for (const mark of marks) {
        const anchor = anchorAt(notes, mark.at, staff);
        if (!anchor) continue;
        // A release after the last note of a bar still lands at its own time
        const offset = Math.round((mark.at - anchor.at) * anchor.divisions);
        for (const type of mark.types) {
            insertDirection(anchor, make(doc, "direction", {placement: "below"},
                make(doc, "direction-type", {},
                    make(doc, "pedal", {type, line: "yes", sign: "yes", "default-y": "-85"})),
                ...(offset ? [make(doc, "offset", {}, String(offset))] : []),
                make(doc, "staff", {}, String(staff))));
        }
    }
}

interface TempoMarks {
    drop: number[];
    fermatas: number[];
    rits: number[];
}

// A short sharp slowdown that snaps back is how a fermata ends up in MIDI;
// a run of small steps down is a rit.
export function tempoMarks(tempos: Timeline["tempos"]): TempoMarks {
    const marks: TempoMarks = {drop: [], fermatas: [], rits: []};
    const rest: Timeline["tempos"] = [tempos[0]];
    for (let i = 1; i < tempos.length; i++) {
        const before = rest[rest.length - 1].bpm;
        const t = tempos[i];
        const back = tempos[i + 1];
        if (t.bpm < 0.75 * before && back && back.at - t.at <= 4.5 && back.bpm >= 0.9 * before) {
            marks.fermatas.push(t.at);
            marks.drop.push(t.at, back.at);
            i++;
            continue;
        }
        rest.push(t);
    }
    const kept: Timeline["tempos"] = [rest[0]];
    for (let i = 1; i < rest.length;) {
        // The first slower step may come long after the last change; the following steps come close together
        let j = rest[i].bpm < rest[i - 1].bpm ? i + 1 : i;
        while (j < rest.length && rest[j].bpm < rest[j - 1].bpm && rest[j].at - rest[j - 1].at <= 1.5) j++;
        if (j - i >= 3) {
            marks.rits.push(rest[i].at);
            for (let k = i; k < j; k++) marks.drop.push(rest[k].at);
            i = j;
        } else {
            kept.push(rest[i]);
            i++;
        }
    }
    // What is left within 5% of the tempo before it is export noise, not a new marking
    for (let i = 1; i < kept.length; i++) {
        if (Math.abs(kept[i].bpm - kept[i - 1].bpm) / kept[i - 1].bpm < 0.05) marks.drop.push(kept[i].at);
    }
    return marks;
}

function applyTempoMarks(doc: Document, l: Layout, marks: TempoMarks) {
    for (const d of l.directions) {
        const sound = child(d.el, "sound");
        const isTempo = sound?.hasAttribute("tempo") || d.el.getElementsByTagName("metronome").length > 0;
        // MuseScore snaps tempo changes to the grid, so the match is loose
        if (isTempo && marks.drop.some(at => Math.abs(at - d.at) < 0.6)) d.el.parentElement?.removeChild(d.el);
    }
    for (const at of marks.fermatas) {
        const sounding = l.notes.filter(n => n.staff === 1 && n.struck && n.at <= at + 0.25 && n.at + n.duration > at);
        const note = sounding.sort((a, b) => b.at - a.at)[0];
        if (note && !note.head.getElementsByTagName("fermata").length) {
            notationsOf(note.head).appendChild(make(doc, "fermata", {type: "upright"}));
        }
    }
    for (const at of marks.rits) {
        const anchor = anchorAt(l.notes, at, 1);
        if (anchor) {
            insertDirection(anchor, make(doc, "direction", {placement: "above"},
                make(doc, "direction-type", {}, make(doc, "words", {"font-style": "italic"}, "rit.")),
                make(doc, "staff", {}, "1")));
        }
    }
}

// Dynamics only when the loudness really changes between passages; a flat file gets none
function addDynamics(doc: Document, l: Layout, accented: Set<Element>) {
    const perMeasure = l.measures.map((_, m) => median(l.notes
        .filter(n => n.measure === m && n.struck && n.velocity !== null && !accented.has(n.head))
        .map(n => n.velocity!)));
    const nearest = (v: number) => DYNAMICS.reduce((a, b) => (Math.abs(b[1] - v) < Math.abs(a[1] - v) ? b : a))[0];
    const levels = perMeasure.map(v => (v ? nearest(v) : null));
    const changes: Array<{ measure: number; mark: string }> = [];
    levels.forEach((mark, m) => {
        // A level counts once it holds for two measures
        if (!mark || levels[m + 1] !== mark) return;
        if (changes[changes.length - 1]?.mark !== mark) changes.push({measure: m, mark});
    });
    if (changes.length < 2) return;
    for (const {measure, mark} of changes) {
        const first = l.notes.filter(n => n.measure === measure && n.staff === 1 && n.el === n.head)[0];
        if (!first) continue;
        insertDirection(first, make(doc, "direction", {placement: "below"},
            make(doc, "direction-type", {}, make(doc, "dynamics", {}, make(doc, mark))),
            make(doc, "staff", {}, "1")));
    }
}

export interface PolishOptions {
    title: string;
    pedals: PedalEvent[];
    tempos: Timeline["tempos"];
}

export function polishImportedScore(source: Document, {title, pedals, tempos}: PolishOptions): Document {
    const doc = source.cloneNode(true) as Document;
    setTitle(doc, title);
    const marks = tempoMarks(tempos);
    for (const part of children(doc.documentElement, "part")) {
        cleanRests(part);
        reassignClefs(part, layout(part));
        const staves = Math.max(1, ...Array.from(part.getElementsByTagName("staves")).map(s => Number(s.textContent)));
        const accented = addAccents(layout(part).notes);
        addDynamics(doc, layout(part), accented);
        applyTempoMarks(doc, layout(part), marks);
        addPedals(doc, layout(part).notes, pedals, staves);
    }
    return doc;
}

// "The_Winner_Takes_It_All(2).mid" → "The Winner Takes It All"
export function titleFromFileName(name: string): string {
    return name
        .replace(/\.[^.]+$/, "")
        .replace(/\s*\(\d+\)$/, "")
        .replace(/[_]+/g, " ")
        .trim();
}
